import type { Backup, LocalStore, SyncClient } from "@client/db/contract";
import { createMemoryStore, createMemorySync } from "./memoryStore";

/**
 * OWNED BY C1. Runtime wiring for the C4 implementations.
 *
 * C4 owns src/client/db and src/client/sync and exports createLocalStore/createSyncClient/createBackup
 * from "@client/db". They are resolved with a dynamic import so this package type-checks and runs
 * before C4 lands; if the factories are missing we fall back to the in-memory store and log once.
 * The coordinator can replace the body of resolveDeps with a static import after the wave.
 */

export interface AppDeps {
  store: LocalStore;
  sync: SyncClient | null;
  backup: Backup | null;
  /** True when the durable Dexie store is in use. The shell warns when it is not. */
  durable: boolean;
}

type DbModule = Partial<{
  createLocalStore: () => LocalStore;
  createSyncClient: (store: LocalStore) => SyncClient;
  createBackup: (store: LocalStore) => Backup;
}>;

let cached: Promise<AppDeps> | null = null;

export function resolveDeps(): Promise<AppDeps> {
  cached ??= (async (): Promise<AppDeps> => {
    let mod: DbModule = {};
    try {
      mod = (await import("@client/db")) as DbModule;
    } catch {
      mod = {};
    }
    if (typeof mod.createLocalStore !== "function") {
      console.warn("[WebCashier] durable local store unavailable; using the in-memory fallback (data will not survive a reload).");
      const store = createMemoryStore();
      return { store, sync: createMemorySync("offline"), backup: null, durable: false };
    }
    const store = mod.createLocalStore();
    await store.open();
    return {
      store,
      sync: typeof mod.createSyncClient === "function" ? mod.createSyncClient(store) : null,
      backup: typeof mod.createBackup === "function" ? mod.createBackup(store) : null,
      durable: true,
    };
  })();
  return cached;
}

/** Tests inject their own deps; this resets the memoised resolution. */
export function resetDeps(): void {
  cached = null;
}
